import { useEffect, useRef, useState } from "react";

/* Shared primitives for the ripp.xyz scroll journey. Canvas loops pause when off-screen. */
export const API = process.env.REACT_APP_BACKEND_URL;
export const C = { cyan: "56,189,248", emerald: "0,229,181", red: "255,51,85", amber: "245,185,66", violet: "167,139,250", text2: "127,139,163", white: "230,237,247" };
export const MONO = "JetBrains Mono, monospace";
export const REDUCED = typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const label = (k = "") => k.replace(/_agent$/, "").replace(/_/g, " ").toUpperCase();
export const fmt = (n, d = 2) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d }));
export const biasRGB = (b) => (b === "bullish" || b === "LONG" ? C.emerald : b === "bearish" || b === "SHORT" ? C.red : C.cyan);
export const clamp01 = (v) => Math.max(0, Math.min(1, v));

export const usePulse = (ms = 8000) => {
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => fetch(`${API}/api/public/pulse`).then((r) => r.json()).then((d) => alive && setPulse(d)).catch(() => {});
    load(); const id = setInterval(load, ms);
    return () => { alive = false; clearInterval(id); };
  }, [ms]);
  return pulse;
};

export const useCanvas = (ref, draw) => {
  const drawRef = useRef(draw); drawRef.current = draw;
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let W = 0, H = 0, raf, on = false; const t0 = performance.now();
    const fit = () => { W = cv.clientWidth; H = cv.clientHeight; cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    fit(); const ro = new ResizeObserver(fit); ro.observe(cv);
    const loop = () => { if (!on) return; ctx.clearRect(0, 0, W, H); drawRef.current(ctx, W, H, ((performance.now() - t0) / 1000) * (REDUCED ? 0.35 : 1)); raf = requestAnimationFrame(loop); };
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && !on) { on = true; loop(); } else if (!e.isIntersecting) { on = false; cancelAnimationFrame(raf); } }, { rootMargin: "240px" });
    io.observe(cv);
    return () => { on = false; cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); };
  }, [ref]);
};

export const mkScale = (bars, box, extra = [], pad = 0.1) => {
  const vals = bars.flatMap((b) => [b.h, b.l]).concat(extra.filter((v) => v != null && Number.isFinite(v)));
  let lo = Math.min(...vals), hi = Math.max(...vals); const r = hi - lo || 1; lo -= r * pad; hi += r * pad;
  return { lo, hi, y: (v) => box.y + box.h - ((v - lo) / (hi - lo)) * box.h, x: (i, n) => box.x + (i / Math.max(1, n - 1)) * box.w };
};

export const drawCandles = (ctx, bars, sc, n, alpha = 0.8, cw = 3) => {
  bars.forEach((b, i) => {
    const up = b.c >= b.o, x = sc.x(i, n), col = up ? C.emerald : C.red;
    ctx.strokeStyle = `rgba(${col},${alpha})`; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, sc.y(b.h)); ctx.lineTo(x, sc.y(b.l)); ctx.stroke();
    ctx.fillStyle = `rgba(${col},${alpha * 0.7})`;
    ctx.fillRect(x - cw / 2, sc.y(Math.max(b.o, b.c)), cw, Math.max(1, Math.abs(sc.y(b.o) - sc.y(b.c))));
  });
};

export const drawStream = (ctx, bars, sc, n, t, rgb = C.cyan, alpha = 0.9) => {
  for (let k = 2; k >= 0; k--) {
    ctx.beginPath();
    bars.forEach((b, i) => { const x = sc.x(i, n), y = sc.y(b.c) + (k ? Math.sin(t * 1.4 + i * 0.3 + k) * k * 1.6 : 0); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    ctx.strokeStyle = `rgba(${rgb},${(k ? 0.18 : alpha)})`; ctx.lineWidth = k ? 4 + k * 3 : 1.6; ctx.stroke();
  }
};

export const hline = (ctx, y, x0, x1, rgb, alpha, dash, text, side = "right") => {
  ctx.setLineDash(dash || []); ctx.strokeStyle = `rgba(${rgb},${alpha})`; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(x1, y); ctx.stroke(); ctx.setLineDash([]);
  if (text) { ctx.fillStyle = `rgba(${rgb},${Math.min(1, alpha + 0.3)})`; ctx.font = `600 9px ${MONO}`; ctx.textAlign = side; ctx.fillText(text, side === "right" ? x1 : x0, y - 4); ctx.textAlign = "left"; }
};

export const Kicker = ({ n, children }) => (
  <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-[#38BDF8]">
    <span className="h-px w-8 bg-[#38BDF8]/60" />{n} — {children}
  </div>
);

export const Section = ({ id, n, kicker, title, sub, children, testid, className = "" }) => (
  <section id={id} data-testid={testid} className={`relative w-full overflow-hidden border-t border-white/[0.06] py-28 ${className}`}>
    <div className="mx-auto max-w-7xl px-6">
      <Kicker n={n}>{kicker}</Kicker>
      <h2 className="mt-5 font-display text-3xl font-black leading-[1.02] tracking-tight text-white md:text-5xl">{title}</h2>
      {sub && <p className="mt-4 max-w-xl font-body text-base font-light leading-relaxed text-white/55 md:text-lg">{sub}</p>}
      {children}
    </div>
  </section>
);

export const Chip = ({ children, rgb = C.cyan, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 font-mono text-[9px] tracking-[0.18em]"
    style={{ color: `rgb(${rgb})`, borderColor: `rgba(${rgb},0.4)`, background: `rgba(${rgb},0.08)` }}>{children}</span>
);

export const Readout = ({ k, v, rgb = C.white, testid }) => (
  <div data-testid={testid}>
    <div className="font-mono text-[8px] tracking-[0.22em] text-[#7F8BA3]">{k}</div>
    <div className="font-mono text-[13px] font-semibold tabular-nums" style={{ color: `rgb(${rgb})` }}>{v ?? "—"}</div>
  </div>
);
